import React, { useState, useEffect, useMemo } from 'react';
import { certificatesApi, assetsApi } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { SlideDrawer } from '../components/ui/slide-drawer';
import { DensityToggle, SortableTh, useDensity, useSortState, clientSort } from '../components/ui/list-controls';
import { Award, Search, Loader2, Plus, ChevronRight, X, FileText, Download, Trash2, Upload, Eye, Check, ChevronsUpDown, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import useConfirm from '../components/useConfirm';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';
import { ReplacesPicker, ReplacementLinks, isSuperseded } from '../components/certificates/CertificateReplacement';

// Client-side status from expiry (60-day expiring window, matching the register spec).
const certStatus = (cert) => {
  if (isSuperseded(cert)) return { key: 'superseded', label: `Superseded${cert.superseded_by_number ? ` by ${cert.superseded_by_number}` : ''}`, tone: 'text-[#6B6558]', pill: 'text-[#6B6558] bg-[#F3F1EC] border-[#DCD7CC]', dot: '#A29D93', days: null };
  if (!cert.expiry_date) return { key: 'valid', label: 'Valid', tone: 'text-[#2F7D3A]', pill: 'text-[#2F7D3A] bg-[#EAF6EC] border-[#BFE3C6]', dot: '#3E9B4F', days: null };
  const days = Math.ceil((new Date(cert.expiry_date) - new Date()) / 86400000);
  if (days < 0) return { key: 'expired', label: `Expired ${Math.abs(days)}d ago`, tone: 'text-[#B91C1C]', pill: 'text-[#B91C1C] bg-[#FDECEA] border-[#F3C4BD]', dot: '#DC2626', days };
  if (days <= 60) return { key: 'expiring', label: days === 0 ? 'Due today' : `Due in ${days}d`, tone: 'text-[#92600A]', pill: 'text-[#92600A] bg-[#FEF6E0] border-[#EBD48F]', dot: '#D9A400', days };
  return { key: 'valid', label: 'Valid', tone: 'text-[#2F7D3A]', pill: 'text-[#2F7D3A] bg-[#EAF6EC] border-[#BFE3C6]', dot: '#3E9B4F', days };
};

export const CertificateRegisterPage = () => {
  const navigate = useNavigate();
  const { formatDate } = useSettings();
  const { confirm, dialogProps } = useConfirm();
  const [certificates, setCertificates] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(null); // 'view' | 'add'
  const [selectedCert, setSelectedCert] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | expired | expiring | valid | missing
  const [typeFilter, setTypeFilter] = useState('all');
  const [density, setDensity] = useDensity('certificates_density');
  const { sortKey, sortDir, toggleSort, setSortKey, setSortDir } = useSortState('expiry', 'asc');
  const [groupBy, setGroupBy] = useState('by_asset'); // 'by_asset' (default) | 'flat'
  const PAGE_SIZE = 25;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const loadData = async () => {
    try {
      const certsRes = await certificatesApi.list({});
      setCertificates(certsRes.data || []);
    } catch (err) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      const all = [];
      for (let offset = 0; ; offset += 100) {
        const { data } = await assetsApi.getAll({ limit: 100, offset });
        all.push(...(data?.items || []));
        if ((data?.items || []).length < 100 || all.length >= (data?.total || 0)) break;
      }
      setAssets(all);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { loadData(); loadAssets(); }, []);

  const openView = async (cert) => {
    setSelectedCert(cert);
    setDrawerMode('view');
    setDrawerOpen(true);
    try {
      const res = await certificatesApi.get(cert.id);
      setSelectedCert(prev => ({ ...prev, ...res.data }));
    } catch { /* keep list row data */ }
  };

  const openAdd = () => {
    setSelectedCert(null);
    setDrawerMode('add');
    setDrawerOpen(true);
  };

  const closeDrawer = () => { setDrawerOpen(false); setTimeout(() => { setDrawerMode(null); setSelectedCert(null); }, 200); };

  const handleDeleteCert = (id) => {
    confirm({
      title: 'Delete this certificate?',
      description: 'This permanently removes the certificate and its attached file. This cannot be undone.',
      confirmLabel: 'Delete',
      testId: 'confirm-delete-certificate',
      onConfirm: async () => {
        try {
          await certificatesApi.delete(id);
          toast.success('Certificate deleted');
          closeDrawer();
          loadData();
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Failed to delete certificate');
          throw err;
        }
      },
    });
  };

  // Enrich once with derived status.
  const enriched = useMemo(
    () => certificates.map(c => ({ ...c, _meta: certStatus(c), _missing: !c.issuing_body })),
    [certificates]
  );

  const current = useMemo(() => enriched.filter(c => c._meta.key !== 'superseded'), [enriched]);
  const bands = useMemo(() => ({
    valid: current.filter(c => c._meta.key === 'valid').length,
    expiring: current.filter(c => c._meta.key === 'expiring').length,
    expired: current.filter(c => c._meta.key === 'expired').length,
    missing: current.filter(c => c._missing).length,
    superseded: enriched.length - current.length,
  }), [enriched, current]);

  const typeOptions = useMemo(() => {
    const counts = {};
    current.forEach(c => { const t = c.certificate_type || 'Other'; counts[t] = (counts[t] || 0) + 1; });
    return Object.entries(counts).map(([value, count]) => ({ value, count }));
  }, [current]);

  const statusCounts = useMemo(() => ({
    all: current.length,
    expired: bands.expired,
    expiring: bands.expiring,
    valid: bands.valid,
    superseded: bands.superseded,
  }), [current, bands]);

  const filtered = useMemo(() => {
    return enriched.filter(cert => {
      const matchesStatus =
        statusFilter === 'all' ? cert._meta.key !== 'superseded' :
        statusFilter === 'missing' ? cert._missing && cert._meta.key !== 'superseded' :
        cert._meta.key === statusFilter;
      const matchesType = typeFilter === 'all' || (cert.certificate_type || 'Other') === typeFilter;
      const q = search.toLowerCase();
      const matchesSearch = !search ||
        cert.certificate_type?.toLowerCase().includes(q) ||
        cert.cert_number?.toLowerCase().includes(q) ||
        cert.asset_name?.toLowerCase().includes(q) ||
        cert.issuing_body?.toLowerCase().includes(q) ||
        cert.component_name?.toLowerCase().includes(q);
      return matchesStatus && matchesType && matchesSearch;
    });
  }, [enriched, statusFilter, typeFilter, search]);

  const sorted = useMemo(() => clientSort(filtered, sortKey, sortDir, {
    cert_number: (c) => c.cert_number,
    certificate_type: (c) => c.certificate_type,
    asset: (c) => c.asset_name,
    issuing_body: (c) => c.issuing_body,
    issued: (c) => (c.issue_date ? new Date(c.issue_date).getTime() : Infinity),
    expiry: (c) => (c.expiry_date ? new Date(c.expiry_date).getTime() : Infinity),
  }), [filtered, sortKey, sortDir]);

  const paged = sorted.slice(0, visibleCount);
  const hasMore = groupBy === 'flat' && sorted.length > visibleCount;
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [search, statusFilter, typeFilter, sortKey, sortDir]);

  // By-asset grouping (default): order by asset name, then by expiry within asset.
  const grouped = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const an = (a.asset_name || 'Unassigned'), bn = (b.asset_name || 'Unassigned');
      if (an !== bn) return an.localeCompare(bn);
      const ax = a.expiry_date ? new Date(a.expiry_date).getTime() : Infinity;
      const bx = b.expiry_date ? new Date(b.expiry_date).getTime() : Infinity;
      return ax - bx;
    });
  }, [filtered]);

  const rows = groupBy === 'by_asset' ? grouped : paged;

  const setPrimarySort = (key) => { setSortKey(key); setSortDir(key === 'expiry' ? 'asc' : 'desc'); };

  const bandCards = [
    { key: 'valid', count: bands.valid, label: 'Valid', dot: '#3E9B4F', num: 'text-[#2F7D3A]' },
    { key: 'expiring', count: bands.expiring, label: 'Expiring within 60 days', dot: '#D9A400', num: 'text-[#92600A]' },
    { key: 'expired', count: bands.expired, label: 'Expired', dot: '#DC2626', num: 'text-[#B91C1C]' },
    { key: 'missing', count: bands.missing, label: 'Missing issuing body', dot: '#A29D93', num: 'text-[#1A1A1A]' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="certificate-register-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-extrabold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Certificate Register
          </h1>
          <p className="text-muted-foreground">Statutory and third-party certification held across your fleet</p>
        </div>
        <Button onClick={openAdd} className="btn-industrial" data-testid="add-certificate-btn">
          <Plus className="w-5 h-5 mr-2" />
          Add Certificate
        </Button>
      </div>

      {/* Band cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {bandCards.map(band => {
          const selected = statusFilter === band.key;
          return (
            <button
              key={band.key}
              type="button"
              onClick={() => setStatusFilter(selected ? 'all' : band.key)}
              data-testid={`cert-band-${band.key}`}
              className={`text-left rounded-xl border p-4 transition-colors ${selected ? 'border-[#C9A400] ring-2 ring-offset-1 ring-[#C9A400] bg-white' : 'border-[#E6E2D9] bg-white hover:border-[#D4CFC2]'}`}
            >
              <div className="flex items-start justify-between">
                <div className={`text-[28px] leading-tight font-extrabold ${band.num}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{band.count}</div>
                <span className="w-2.5 h-2.5 rounded-full mt-2" style={{ background: band.dot }} />
              </div>
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8A857A] mt-1">{band.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filters + list — single card (gold standard) */}
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border space-y-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center justify-between">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Cert number, asset or issuing body…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-10 pr-14"
                data-testid="cert-search"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-muted-foreground">{filtered.length}/{current.length}</span>
            </div>
            <div className="flex items-center gap-3">
              <DensityToggle density={density} onChange={setDensity} />
              <div className="flex items-center gap-1 bg-secondary rounded-lg p-1" data-testid="cert-groupby-toggle">
                {[{ id: 'by_asset', label: 'By asset' }, { id: 'flat', label: 'Flat' }].map(opt => (
                  <button key={opt.id} type="button" onClick={() => setGroupBy(opt.id)} data-testid={`cert-groupby-${opt.id}`}
                    className={`px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${groupBy === opt.id ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Sort</span>
                <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1" data-testid="cert-sort-toggle">
                  {[{ id: 'expiry', label: 'Expiry' }, { id: 'issued', label: 'Issued' }].map(opt => {
                    const active = sortKey === opt.id;
                    return (
                      <button key={opt.id} type="button" onClick={() => setPrimarySort(opt.id)}
                        data-testid={`cert-sort-${opt.id}`}
                        className={`px-3 py-1.5 rounded-md text-[13px] font-semibold uppercase tracking-wide transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Status + Type pills */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Status</span>
              <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1" data-testid="cert-status-filter">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'expired', label: 'Expired', dot: '#DC2626' },
                  { id: 'expiring', label: 'Expiring', dot: '#D9A400' },
                  { id: 'valid', label: 'Valid', dot: '#3E9B4F' },
                  { id: 'superseded', label: 'Superseded / history', dot: '#A29D93' },
                ].map(s => {
                  const active = statusFilter === s.id;
                  return (
                    <button key={s.id} type="button" onClick={() => setStatusFilter(s.id)} data-testid={`cert-status-${s.id}`}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                      <span className="inline-flex items-center gap-1.5">
                        {s.dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />}
                        {s.label}
                      </span>
                      <span className={`text-[11px] font-bold ${active ? 'text-black/70' : 'text-muted-foreground'}`}>{statusCounts[s.id] ?? 0}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {typeOptions.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Type</span>
                <div className="inline-flex items-center gap-1 bg-secondary rounded-lg p-1 flex-wrap" data-testid="cert-type-filter">
                  <button type="button" onClick={() => setTypeFilter('all')} data-testid="cert-type-all"
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${typeFilter === 'all' ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                    All <span className={`text-[11px] font-bold ${typeFilter === 'all' ? 'text-black/70' : 'text-muted-foreground'}`}>{current.length}</span>
                  </button>
                  {typeOptions.map(t => {
                    const active = typeFilter === t.value;
                    return (
                      <button key={t.value} type="button" onClick={() => setTypeFilter(t.value)} title={t.value}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${active ? 'bg-[#FFD108] text-black shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                        <span className="max-w-[140px] truncate">{t.value}</span>
                        <span className={`text-[11px] font-bold ${active ? 'text-black/70' : 'text-muted-foreground'}`}>{t.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Award className="w-16 h-16 mx-auto text-muted-foreground mb-4 opacity-60" />
            <h3 className="text-xl font-semibold mb-2">No Certificates Found</h3>
            <p className="text-muted-foreground mb-4">
              {enriched.length === 0 ? 'Add certificates from an asset’s Certificates tab.' : 'No certificates match these filters.'}
            </p>
            {enriched.length > 0 && (statusFilter !== 'all' || typeFilter !== 'all' || search) && (
              <Button variant="outline" onClick={() => { setStatusFilter('all'); setTypeFilter('all'); setSearch(''); }}>Clear filters</Button>
            )}
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="data-table w-full" data-density={density}>
              <thead>
                <tr className="border-b border-border">
                  <SortableTh sortKey="cert_number" label="Cert #" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTh sortKey="certificate_type" label="Type" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTh sortKey="asset" label="Asset / Component" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTh sortKey="issuing_body" label="Issuing Body" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTh sortKey="issued" label="Issued" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <SortableTh sortKey="expiry" label="Expiry / Status" current={sortKey} dir={sortDir} onSort={toggleSort} />
                  <th className="p-4" />
                </tr>
              </thead>
              <tbody>
                {rows.map((cert, idx, arr) => {
                  const m = cert._meta;
                  const assetName = cert.asset_name || 'Unassigned';
                  const showHeader = groupBy === 'by_asset' && (idx === 0 || (arr[idx - 1].asset_name || 'Unassigned') !== assetName);
                  return (
                    <React.Fragment key={cert.id}>
                    {showHeader && (
                      <tr className="bg-[#FBFAF7] border-b border-[#E6E2D9]" data-testid={`cert-group-${cert.asset_id || 'none'}`}>
                        <td colSpan={7} className="px-4 py-2">
                          <span className="font-semibold text-[#2A2A2A] text-[12px]">{assetName}</span>
                          <span className="text-muted-foreground text-[12px]"> · {arr.filter(x => (x.asset_name || 'Unassigned') === assetName).length} certificates</span>
                        </td>
                      </tr>
                    )}
                    <tr
                      className="border-b border-border hover:bg-secondary/50 cursor-pointer transition-colors"
                      onClick={() => openView(cert)}
                      data-testid={`cert-row-${cert.id}`}
                    >
                      <td className="p-4"><span className="font-mono text-[13px] text-[#C9A400] font-semibold">{cert.cert_number || '—'}</span></td>
                      <td className="p-4"><span className="text-[14px] text-[#1A1A1A]">{cert.certificate_type || '—'}</span></td>
                      <td className="p-4">
                        <div className="text-[14px] text-[#1A1A1A]">{cert.asset_name || '—'}</div>
                        {cert.component_name && <div className="text-[11px] font-mono text-muted-foreground">{cert.component_name}</div>}
                      </td>
                      <td className="p-4">
                        {cert.issuing_body
                          ? <span className="text-[14px] text-[#4A463E]">{cert.issuing_body}</span>
                          : <span className="text-[13px] italic text-[#B5AFA3]">Not recorded</span>}
                      </td>
                      <td className="p-4"><span className="font-mono text-[12px] text-[#6B6558]">{cert.issue_date ? formatDate(cert.issue_date) : '—'}</span></td>
                      <td className="p-4">
                        <div className={`font-mono text-[12px] ${m.key === 'expired' ? 'text-[#B91C1C]' : m.key === 'expiring' ? 'text-[#92600A]' : 'text-[#1A1A1A]'}`}>
                          {cert.expiry_date ? formatDate(cert.expiry_date) : '—'}
                        </div>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border mt-1 ${m.pill}`}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} />{m.label}
                        </span>
                      </td>
                      <td className="p-4 text-right"><ChevronRight className="w-4 h-4 text-muted-foreground inline" /></td>
                    </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-border" data-testid="cert-result-count">
            <span className="text-sm text-muted-foreground">Showing {rows.length} of {sorted.length} certificates</span>
            <div className="flex items-center gap-3">
              {hasMore && (
                <Button variant="outline" size="sm" onClick={() => setVisibleCount(c => c + PAGE_SIZE)} data-testid="cert-load-more-btn">
                  Load More ({sorted.length - paged.length} more)
                </Button>
              )}
              <span className="text-sm text-muted-foreground">{bands.expired} expired · {bands.expiring} expiring within 60 days</span>
            </div>
          </div>
          </>
        )}
      </Card>

      <SlideDrawer
        open={drawerOpen}
        onOpenChange={closeDrawer}
        title={drawerMode === 'add' ? 'Add Certificate' : (selectedCert?.cert_number ? `Certificate ${selectedCert.cert_number}` : 'Certificate')}
        size="lg"
        className={drawerMode === 'view' ? 'sheet-ground' : undefined}
        dismissable={drawerMode === 'view'}
      >
        {drawerMode === 'view' && selectedCert ? (
          <CertificateDetailView
            cert={selectedCert}
            certs={certificates}
            formatDate={formatDate}
            onDelete={() => handleDeleteCert(selectedCert.id)}
            onChanged={async () => {
              try { const res = await certificatesApi.get(selectedCert.id); setSelectedCert(prev => ({ ...prev, ...res.data })); } catch { /* noop */ }
              loadData();
            }}
          />
        ) : drawerMode === 'add' ? (
          <AddCertificateForm
            assets={assets}
            certs={certificates}
            onCancel={closeDrawer}
            onSuccess={() => { closeDrawer(); loadData(); }}
          />
        ) : null}
      </SlideDrawer>
      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

// ---- Certificate detail sheet (Gold Standard) ----
const CertificateDetailView = ({ cert, certs, formatDate, onDelete, onChanged }) => {
  const m = certStatus(cert);
  const { confirm, dialogProps } = useConfirm();
  const [busy, setBusy] = useState(false);

  const viewFile = async () => {
    setBusy(true);
    try {
      const res = await certificatesApi.downloadFile(cert.id);
      const url = window.URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch { toast.error('Could not open the file'); } finally { setBusy(false); }
  };

  const removeFile = () => {
    confirm({
      title: 'Remove the attached file?',
      description: 'The certificate stays; only its attached document is removed.',
      confirmLabel: 'Remove file',
      testId: 'confirm-remove-cert-file',
      onConfirm: async () => {
        try { await certificatesApi.deleteFile(cert.id); toast.success('File removed'); onChanged && onChanged(); }
        catch { toast.error('Could not remove the file'); throw new Error('remove failed'); }
      },
    });
  };

  const attachFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10 MB'); return; }
    const reader = new FileReader();
    reader.onloadend = async () => {
      try { await certificatesApi.uploadFile(cert.id, reader.result, file.name, file.type); toast.success('File attached'); onChanged && onChanged(); }
      catch { toast.error('Could not attach the file'); }
    };
    reader.readAsDataURL(file);
  };

  const details = [
    { label: 'Asset', value: cert.asset_name || '—' },
    { label: 'Component', value: cert.component_name || '—' },
    { label: 'Type', value: cert.certificate_type || '—' },
    { label: 'Issuing body', value: cert.issuing_body || 'Not recorded' },
    { label: 'Issued', value: cert.issue_date ? formatDate(cert.issue_date) : '—' },
    { label: 'Expiry date', value: cert.expiry_date ? formatDate(cert.expiry_date) : 'No expiry' },
    { label: 'Visibility', value: cert.is_public ? 'Public on QR page' : 'Internal only' },
  ];
  const fileInputRef = React.useRef(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-lg border border-[#E6E2D9] bg-white flex items-center justify-center shrink-0">
          <Award className="w-7 h-7 text-[#C9A400]" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-[22px] font-bold text-[#1A1A1A] leading-tight break-words" data-testid="cert-detail-type">{cert.certificate_type || 'Certificate'}</h2>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className="font-mono text-[12px] text-[#C9A400] font-semibold">{cert.cert_number}</span>
            <span className="text-[12px] text-muted-foreground">· {cert.asset_name || '—'}</span>
          </div>
        </div>
      </div>

      {cert.expiry_date && (
        <div className={`flex items-center justify-between rounded-lg border-l-4 border px-4 py-3 ${m.pill}`} data-testid="cert-expiry-banner">
          <span className="inline-flex items-center gap-2 text-[14px] font-semibold">
            <span className="w-2 h-2 rounded-full" style={{ background: m.dot }} />
            {m.label}
          </span>
          <span className="text-[13px] font-mono">{formatDate(cert.expiry_date)}</span>
        </div>
      )}

      <div className="id-card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#EFEBE3]">
          <span className="text-[10.5px] font-bold uppercase tracking-[0.11em] text-[#8A857A]">Details</span>
        </div>
        <div>
          {details.map((row, i) => (
            <div key={row.label} className={`flex items-start gap-4 px-4 py-3 ${i < details.length - 1 ? 'border-b border-[#EFEBE3]' : ''}`}>
              <span className="w-36 shrink-0 text-[14px] text-[#6B6558]">{row.label}</span>
              <span className="flex-1 text-[14px] text-[#1A1A1A]">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {cert.notes && (
        <div className="id-card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#EFEBE3]"><span className="text-[10.5px] font-bold uppercase tracking-[0.11em] text-[#8A857A]">Notes</span></div>
          <div className="px-4 py-3 text-[14px] text-[#4A463E] whitespace-pre-wrap">{cert.notes}</div>
        </div>
      )}

      <div className="id-card p-0 overflow-hidden" data-testid="cert-history-card">
        <div className="px-4 py-3 border-b border-[#EFEBE3]"><span className="text-[10.5px] font-bold uppercase tracking-[0.11em] text-[#8A857A]">Replacement history</span></div>
        <div className="px-4 py-3 space-y-2">
          <CertChain cert={cert} certs={certs} formatDate={formatDate} />
          <ReplacementLinks cert={cert} certs={certs} onChanged={onChanged} />
        </div>
      </div>

      {/* Attached document */}
      <div className="id-card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#EFEBE3]"><span className="text-[10.5px] font-bold uppercase tracking-[0.11em] text-[#8A857A]">Document</span></div>
        <div className="px-4 py-3">
          <input ref={fileInputRef} type="file" className="hidden" onChange={attachFile} data-testid="cert-file-input" />
          {cert.has_file ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-[#C9A400]" /></div>
              <div className="flex-1 text-[14px] text-[#1A1A1A]">Certificate file attached</div>
              <Button variant="outline" size="sm" disabled={busy} onClick={viewFile} data-testid="cert-view-file"><Eye className="w-4 h-4 mr-2" />View</Button>
              <Button variant="outline" size="sm" onClick={removeFile} className="text-[#B91C1C] border-[#F3C4BD] hover:bg-[#FFEDEA]" data-testid="cert-remove-file"><Trash2 className="w-4 h-4" /></Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <span className="text-[14px] text-muted-foreground">No file attached</span>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="cert-attach-file"><Upload className="w-4 h-4 mr-2" />Attach file</Button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-[#EFEBE3]">
        {cert.has_file && (
          <Button onClick={viewFile} disabled={busy} className="btn-industrial" data-testid="cert-download-btn"><Download className="w-4 h-4 mr-2" />View document</Button>
        )}
        <Button variant="outline" onClick={onDelete} className="ml-auto text-[#B91C1C] border-[#F3C4BD] hover:bg-[#FFEDEA]" data-testid="cert-delete-btn"><Trash2 className="w-4 h-4 mr-2" />Delete</Button>
      </div>
      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

// Full replacement chain (oldest -> newest) built from the loaded register.
const CertChain = ({ cert, certs, formatDate }) => {
  const byId = Object.fromEntries((certs || []).map(c => [c.id, c]));
  let first = byId[cert.id] || cert;
  const seen = new Set([first.id]);
  while (first.replaces_cert_id && byId[first.replaces_cert_id] && !seen.has(first.replaces_cert_id)) { first = byId[first.replaces_cert_id]; seen.add(first.id); }
  const chain = [first];
  while (chain[chain.length - 1].superseded_by && byId[chain[chain.length - 1].superseded_by] && chain.length < 50) chain.push(byId[chain[chain.length - 1].superseded_by]);
  if (chain.length < 2) return <p className="text-[13px] text-muted-foreground">Not linked to any other certificate.</p>;
  return (
    <ol className="space-y-1" data-testid="cert-chain">
      {chain.map(c => (
        <li key={c.id} className={`flex items-center gap-2 text-[13px] ${c.id === cert.id ? 'font-semibold text-[#1A1A1A]' : 'text-[#6B6558]'}`}>
          <span className="font-mono">{c.cert_number}</span>
          <span>{c.expiry_date ? `exp ${formatDate(c.expiry_date)}` : 'no expiry'}</span>
          <span className="text-[11px] uppercase tracking-wide">{isSuperseded(c) ? 'superseded' : 'current'}</span>
        </li>
      ))}
    </ol>
  );
};

// ---- Add certificate form (in-sheet) ----
const AddCertificateForm = ({ assets, certs, onCancel, onSuccess }) => {
  const { formatDate } = useSettings();
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ asset_id: '', certificate_type: '', issuing_body: '', notes: '' });
  const [issueDate, setIssueDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [file, setFile] = useState(null);
  const [replacesId, setReplacesId] = useState(null);
  const fileRef = React.useRef(null);
  const selectedAsset = assets.find(a => a.id === form.asset_id);
  const assetCerts = (certs || []).filter(c => form.asset_id && c.asset_id === form.asset_id);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('File must be under 10 MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setFile({ name: f.name, type: f.type, size: f.size, data: reader.result });
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!form.asset_id) { toast.error('Choose an asset'); return; }
    if (!form.certificate_type.trim()) { toast.error('Certificate type is required'); return; }
    setSaving(true);
    try {
      const res = await certificatesApi.create({
        asset_id: form.asset_id,
        certificate_type: form.certificate_type.trim(),
        issuing_body: form.issuing_body.trim() || null,
        issue_date: issueDate ? issueDate.toISOString() : null,
        expiry_date: expiryDate ? expiryDate.toISOString() : null,
        notes: form.notes.trim() || null,
        replaces_cert_id: replacesId || null,
      });
      const newId = res.data?.id;
      if (file && newId) {
        await certificatesApi.uploadFile(newId, file.data, file.name, file.type);
      }
      toast.success('Certificate added');
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add certificate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label className="label-overline">Asset *</Label>
        <Popover open={assetPickerOpen} onOpenChange={setAssetPickerOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="cert-asset-select">
              {selectedAsset ? `${selectedAsset.unit_number ? selectedAsset.unit_number + ' · ' : ''}${selectedAsset.name}` : 'Select asset…'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command filter={(value, s) => { const a = assets.find(x => x.id === value); if (!a) return 0; return `${a.name} ${a.unit_number || ''} ${a.serial_number || ''}`.toLowerCase().includes(s.toLowerCase()) ? 1 : 0; }}>
              <CommandInput placeholder="Search by name, unit #, serial…" data-testid="cert-asset-search" />
              <CommandEmpty>No asset matches.</CommandEmpty>
              <CommandList>
                <CommandGroup>
                  {assets.map(a => (
                    <CommandItem key={a.id} value={a.id} onSelect={(v) => { setForm(prev => ({ ...prev, asset_id: v })); setAssetPickerOpen(false); }} data-testid={`cert-asset-option-${a.id}`}>
                      <Check className={`mr-2 h-4 w-4 ${form.asset_id === a.id ? 'opacity-100' : 'opacity-0'}`} />
                      <div className="min-w-0">
                        <p className="truncate">{a.unit_number ? <span className="font-mono text-xs text-volt mr-1.5">{a.unit_number}</span> : null}{a.name}</p>
                        {(a.make || a.serial_number) && <p className="text-[11px] text-muted-foreground truncate">{[a.make, a.model, a.serial_number].filter(Boolean).join(' · ')}</p>}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="space-y-1.5">
        <Label className="label-overline">Type *</Label>
        <Input value={form.certificate_type} onChange={e => setForm({ ...form, certificate_type: e.target.value })} placeholder="e.g. Annual Thorough Examination" data-testid="cert-type-input" />
      </div>

      <div className="space-y-1.5">
        <Label className="label-overline">Issuing Body</Label>
        <Input value={form.issuing_body} onChange={e => setForm({ ...form, issuing_body: e.target.value })} placeholder="e.g. SafeLift Pty Ltd" data-testid="cert-issuing-input" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="label-overline">Issued</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start font-normal" data-testid="cert-issue-date"><CalendarIcon className="w-4 h-4 mr-2" />{issueDate ? formatDate(issueDate) : 'Pick date'}</Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={issueDate} onSelect={setIssueDate} initialFocus /></PopoverContent>
          </Popover>
        </div>
        <div className="space-y-1.5">
          <Label className="label-overline">Expiry</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start font-normal" data-testid="cert-expiry-date"><CalendarIcon className="w-4 h-4 mr-2" />{expiryDate ? formatDate(expiryDate) : 'Pick date'}</Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={expiryDate} onSelect={setExpiryDate} initialFocus /></PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="label-overline">Notes</Label>
        <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Optional notes…" data-testid="cert-notes-input" />
      </div>

      <ReplacesPicker key={form.asset_id || 'none'} certs={assetCerts} certificateType={form.certificate_type} value={replacesId} onChange={setReplacesId} />

      <div className="space-y-1.5">
        <Label className="label-overline">Certificate file</Label>
        <input ref={fileRef} type="file" className="hidden" onChange={pickFile} data-testid="cert-add-file-input" />
        <Button type="button" variant="outline" className="w-full justify-start font-normal" onClick={() => fileRef.current?.click()} data-testid="cert-add-choose-file">
          <Upload className="w-4 h-4 mr-2" />{file ? file.name : 'Attach a file (optional)'}
        </Button>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t border-[#EFEBE3]">
        <Button onClick={submit} disabled={saving} className="btn-industrial" data-testid="cert-submit-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}Add certificate
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="cert-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
};

export default CertificateRegisterPage;
